import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Scale, ChevronDown, ChevronUp, Plus, TrendingDown, TrendingUp, Minus, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { useSmartComment } from '../hooks/useSmartComment';

const API = process.env.REACT_APP_BACKEND_URL;

export function BodyTracker({ language }) {
    const [expanded, setExpanded] = useState(false);
    const [entries, setEntries] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ weight_kg: '', body_fat_pct: '', muscle_mass_kg: '', body_water_l: '' });
    const { fetchComment } = useSmartComment();
    const t = language === 'de';

    useEffect(() => { if (expanded) loadEntries(); }, [expanded]);

    const loadEntries = async () => {
        try {
            const res = await axios.get(`${API}/api/health/body-analysis`);
            setEntries(Array.isArray(res.data) ? res.data : []);
        } catch(e) {}
    };

    const handleSave = async () => {
        if (!form.weight_kg) return;
        setLoading(true);
        try {
            const data = {
                source: 'scale',
                weight_kg: parseFloat(form.weight_kg),
                body_fat_pct: form.body_fat_pct ? parseFloat(form.body_fat_pct) : null,
                muscle_mass_kg: form.muscle_mass_kg ? parseFloat(form.muscle_mass_kg) : null,
                body_water_l: form.body_water_l ? parseFloat(form.body_water_l) : null,
                date: new Date().toISOString()
            };
            await axios.post(`${API}/api/health/body-analysis`, data);
            toast.success(t ? 'Körperdaten gespeichert! +10 XP' : 'Body data saved! +10 XP');
            setForm({ weight_kg: '', body_fat_pct: '', muscle_mass_kg: '', body_water_l: '' });
            setShowAdd(false);
            loadEntries();
            
            // Smart comment based on weight change
            const latestBefore = entries[0];
            if (latestBefore) {
                const diff = (parseFloat(data.weight_kg) - latestBefore.weight_kg).toFixed(1);
                const direction = parseFloat(diff) < 0 ? 'abgenommen' : parseFloat(diff) > 0 ? 'zugenommen' : 'stabil';
                fetchComment('weight', `Gewicht: ${data.weight_kg}kg (${diff > 0 ? '+' : ''}${diff}kg ${direction}). KFA: ${data.body_fat_pct || '?'}%`, language);
            } else {
                fetchComment('weight', `Erste Körpermessung: ${data.weight_kg}kg`, language);
            }
        } catch(e) { toast.error('Error'); }
        finally { setLoading(false); }
    };

    // Latest entry for summary
    const latest = entries[0];
    const prev = entries[1];
    const weightDiff = latest && prev ? (latest.weight_kg - prev.weight_kg).toFixed(1) : null;

    return (
        <Card className="border" data-testid="body-tracker">
            <CardContent className="p-0">
                {/* Collapsed header */}
                <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-center justify-between text-left">
                    <div className="flex items-center gap-2">
                        <Scale className="w-4 h-4 text-violet-500" />
                        <span className="font-semibold text-sm">{t ? 'Körperdaten' : 'Body Data'}</span>
                        {latest && (
                            <span className="text-xs text-muted-foreground ml-1">
                                {latest.weight_kg} kg
                                {weightDiff && weightDiff !== '0.0' && (
                                    <span className={`ml-1 ${parseFloat(weightDiff) < 0 ? 'text-green-600' : 'text-orange-500'}`}>
                                        ({parseFloat(weightDiff) > 0 ? '+' : ''}{weightDiff})
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                    {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>

                {expanded && (
                    <div className="px-4 pb-4 space-y-3">
                        {/* Quick add button */}
                        <Button size="sm" variant="outline" className="w-full gap-1 text-xs" onClick={() => setShowAdd(!showAdd)}>
                            <Plus className="w-3 h-3" /> {t ? 'Neue Messung' : 'New Measurement'}
                        </Button>

                        {/* Add form */}
                        {showAdd && (
                            <div className="p-3 rounded-xl bg-muted/50 border border-border space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-[10px] text-muted-foreground">{t ? 'Gewicht' : 'Weight'} (kg) *</label>
                                        <Input type="number" step="0.1" value={form.weight_kg}
                                            onChange={(e) => setForm({...form, weight_kg: e.target.value})}
                                            placeholder="74.5" className="h-9 bg-card border-2 border-border text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-muted-foreground">{t ? 'Körperfett' : 'Body Fat'} (%)</label>
                                        <Input type="number" step="0.1" value={form.body_fat_pct}
                                            onChange={(e) => setForm({...form, body_fat_pct: e.target.value})}
                                            placeholder="21.0" className="h-9 bg-card border-2 border-border text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-muted-foreground">{t ? 'Muskelmasse' : 'Muscle'} (kg)</label>
                                        <Input type="number" step="0.1" value={form.muscle_mass_kg}
                                            onChange={(e) => setForm({...form, muscle_mass_kg: e.target.value})}
                                            placeholder="32.8" className="h-9 bg-card border-2 border-border text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-muted-foreground">{t ? 'Wasser' : 'Water'} (L)</label>
                                        <Input type="number" step="0.1" value={form.body_water_l}
                                            onChange={(e) => setForm({...form, body_water_l: e.target.value})}
                                            placeholder="43.4" className="h-9 bg-card border-2 border-border text-sm" />
                                    </div>
                                </div>
                                <Button size="sm" onClick={handleSave} disabled={loading || !form.weight_kg} className="w-full gap-1">
                                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                    {t ? 'Speichern' : 'Save'}
                                </Button>
                            </div>
                        )}

                        {/* History */}
                        {entries.length === 0 ? (
                            <p className="text-xs text-muted-foreground text-center py-2">{t ? 'Noch keine Messungen' : 'No measurements yet'}</p>
                        ) : (
                            <div className="space-y-1">
                                {entries.slice(0, 10).map((e, i) => {
                                    const prevEntry = entries[i + 1];
                                    const diff = prevEntry ? (e.weight_kg - prevEntry.weight_kg).toFixed(1) : null;
                                    const date = e.date ? e.date.slice(0, 10) : (e.logged_at ? e.logged_at.slice(0, 10) : '?');
                                    return (
                                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 text-xs">
                                            <span className="text-muted-foreground w-16 shrink-0">{date.slice(5)}</span>
                                            <span className="font-bold w-14">{e.weight_kg} kg</span>
                                            {diff && diff !== '0.0' && (
                                                <span className={`flex items-center gap-0.5 ${parseFloat(diff) < 0 ? 'text-green-600' : 'text-orange-500'}`}>
                                                    {parseFloat(diff) < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                                    {parseFloat(diff) > 0 ? '+' : ''}{diff}
                                                </span>
                                            )}
                                            {diff === '0.0' && <Minus className="w-3 h-3 text-muted-foreground" />}
                                            <span className="text-muted-foreground ml-auto">
                                                {e.body_fat_pct ? `${e.body_fat_pct}%` : ''}
                                                {e.muscle_mass_kg ? ` ${e.muscle_mass_kg}kg` : ''}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
